import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { isEqual, keyBy, mapValues } from 'lodash';

import { Box, useMediaQuery } from '@mui/material';
import { Layout, Loading } from '../components';
import { ListsTitle } from './AuctionsPage';
import { PreferencesFilters } from '../components/PreferencesFilters';

import { SetRecommendedCarsNotificationsCount } from '../store/actions/auction.actions';

import { preferencesLabel } from '../constants/preferencesLabel';
import {
  createUserPreferences,
  deleteUserPreferences,
  listUserPreferences,
  updateUserPreferences,
} from '../store/orderCars/effects';
import { enableTooltip } from '../store/tooltip/actions';
import { invertKeysToValues } from '../utils/array';
import { fetchRelatedDealers } from '../store/actions/user.actions';
import styled from 'styled-components';

const preferenceObj = invertKeysToValues(preferencesLabel);

const formatRequest = obj =>
  mapValues(
    keyBy(obj, o => preferenceObj[o.name]),
    'isActive'
  );

const sortName = (a, b) => {
  const nameA = a.name.toUpperCase();
  const nameB = b.name.toUpperCase();

  let comp = 0;
  if (nameA > nameB) {
    comp = 1;
  } else if (nameA < nameB) {
    comp = -1;
  }
  return comp;
};

const NewTypography = styled.span`
  font-family: ${({ fontFamily }) => fontFamily ?? 'Roboto'};
  font-weight: ${({ fontWeight }) => fontWeight ?? 700};
  font-size: ${({ fontSize }) => fontSize ?? 18}px;
  color: ${({ color }) => color ?? '#2274a5'};
`;

const MyDivider = () => <Box height={'1px'} sx={{ backgroundColor: '#D9D9D9' }} />;

const RECEIVER_ORIGINAL_STATE = {
  whatsapp: false,
  email: false,
  push_notification: false,
};

export function PreferencesPage() {
  const [loading, setLoading] = useState(false);
  const [selectedDealer, setSelectedDealer] = useState(null);

  const {
    loaded: userPreferencesLoaded,
    userPreferences,
    loading: userPreferencesLoading,
  } = useSelector(state => state.orderCars);
  const { user } = useSelector(state => state.authentication);
  const { relatedDealers, relatedDealersLoading } = useSelector(state => state.user);
  const dispatch = useDispatch();

  const history = useHistory();
  const isMobileSize = useMediaQuery('(max-width: 832px)');

  // Verificar se veio pelo fluxo de recadastro
  const isRecadastro = new URLSearchParams(history.location.search).get('recadastro') === 'true';

  const hero = user.isNinja()
    ? {
        title: 'Recomendados para você - Ativar/alterar preferências',
      }
    : {
        title: 'Perfil de Ofertas',
      };

  useEffect(() => {
    if (!userPreferencesLoaded) dispatch(listUserPreferences(user.id));
  }, []);

  useEffect(() => {
    if (!!selectedDealer) dispatch(listUserPreferences(selectedDealer));
  }, [selectedDealer]);

  useEffect(() => {
    if (user.isNinja()) {
      dispatch(fetchRelatedDealers());
    }
  }, []);

  const handleSave = async data => {
    setLoading(true);

    console.log('PreferencesPage - handleSave chamado');
    console.log('PreferencesPage - userPreferences:', userPreferences);
    console.log('PreferencesPage - userPreferences.id:', userPreferences?.id);

    const { preferences: formattedPreferences, receivers: channelOfContactFormatted, estimatedNotifications } = data;

    const sorttedPreferences = [...(userPreferences?.preferences || [])].sort(sortName);
    const sortedFormattedPreferences = [...formattedPreferences].sort(sortName);

    const preferencesAreEqual = isEqual(sortedFormattedPreferences, sorttedPreferences);
    const receiversAreEqual = isEqual(
      channelOfContactFormatted.reduce((acc, r) => {
        acc[r.name.toLowerCase()] = r.isActive;
        return acc;
      }, {}),
      {
        ...RECEIVER_ORIGINAL_STATE,
        ...userPreferences?.channelOfContact,
      }
    );

    const preferencesAreSelected = formattedPreferences.some(p => p.isActive);

    const id = !!selectedDealer ? selectedDealer : user.id;
    // eslint-disable-next-line prettier/prettier
    const relatedNinja = user.isNinja() ? (id === user.id ? '' : user?.email) : '';

    // Se veio pelo fluxo de recadastro, sempre redireciona para / após salvar
    const isRecadastro = new URLSearchParams(history.location.search).get('recadastro') === 'true';
    console.log('PreferencesPage - isRecadastro:', isRecadastro);
    console.log(
      'PreferencesPage - URL atual:',
      history.location.pathname + history.location.search
    );

    try {
      if (user.isNinja() && !userPreferences?.preferences) {
        await dispatch(
          createUserPreferences(id, channelOfContactFormatted, formattedPreferences, relatedNinja, estimatedNotifications)
        );
        setLoading(false);
        // Se for recadastro, sempre vai para /
        console.log('PreferencesPage - Redirecionando após criar preferências (ninja)');
        if (isRecadastro) {
          console.log('PreferencesPage - Usando window.location para recadastro');
          window.location.href = '/';
        } else {
          history.push('/');
        }
      } else if (preferencesAreEqual && receiversAreEqual && !isRecadastro) {
        // Só usa goBack se não for recadastro
        setLoading(false);
        console.log('PreferencesPage - Preferências iguais, usando goBack()');
        history.goBack();
      } else if (preferencesAreSelected) {
        // Verifica se tem ID antes de atualizar, se não tiver, cria
        if (!userPreferences?.id) {
          console.log('PreferencesPage - Não tem ID, criando preferências');
          await dispatch(
            createUserPreferences(id, channelOfContactFormatted, formattedPreferences, relatedNinja, estimatedNotifications)
          );
        } else {
          await dispatch(
            updateUserPreferences(
              userPreferences.id,
              id,
              channelOfContactFormatted,
              formattedPreferences,
              relatedNinja,
              estimatedNotifications
            )
          );
        }
        setLoading(false);
        // Se for recadastro, sempre vai para /
        console.log('PreferencesPage - Redirecionando após atualizar preferências');
        if (isRecadastro) {
          console.log('PreferencesPage - Usando window.location para recadastro');
          // Força o redirecionamento usando window.location para garantir que funcione
          window.location.href = '/';
        } else {
          history.push('/');
        }
      } else {
        await dispatch(deleteUserPreferences(userPreferences.id));
        await dispatch(SetRecommendedCarsNotificationsCount());
        dispatch(enableTooltip({ messageId: 9 }));
        setLoading(false);
        // Se for recadastro, sempre vai para /
        console.log('PreferencesPage - Redirecionando após deletar preferências');
        if (isRecadastro) {
          console.log('PreferencesPage - Usando window.location para recadastro');
          window.location.href = '/';
        } else {
          history.push('/');
        }
      }
    } catch (error) {
      setLoading(false);
      console.error('PreferencesPage - Erro ao salvar:', error);
    }
  };

  const handleCancel = () => {
    history.goBack();
  };

  const load = userPreferencesLoading || relatedDealersLoading;

  // Para ninjas, precisamos carregar as preferências do dealer selecionado
  const getInitialPreferences = () => {
    if (user.isNinja() && selectedDealer && userPreferences?.preferences) {
      return userPreferences;
    }
    return userPreferences;
  };

  return (
    <Layout>
      {!user.isNinja() && !userPreferencesLoaded ? (
        <Box display="flex" alignItems="center" justifyContent="center" width="100%">
          <Loading />
        </Box>
      ) : (
        <Box
          display="flex"
          flexDirection="column"
          maxWidth={1300}
          width="100%"
          marginY={8}
          marginX="auto"
          paddingX={3}
        >
          {user.isNinja() && load ? (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              width="100%"
              height="100%"
            >
              <Loading />
            </Box>
          ) : (
            <PreferencesFilters
              onSave={handleSave}
              onCancel={handleCancel}
              initialPreferences={getInitialPreferences()}
              userId={!!selectedDealer ? selectedDealer : user.id}
              relatedNinja={user.isNinja() ? (selectedDealer === user.id ? '' : user?.email) : ''}
              isRecadastro={isRecadastro}
              isNinja={user.isNinja()}
              relatedDealers={relatedDealers}
              selectedDealer={selectedDealer}
              onDealerChange={evt => setSelectedDealer(evt.target.value)}
            />
          )}
        </Box>
      )}
    </Layout>
  );
}
